import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Tag, Save, Loader2, Sparkles, Hash } from 'lucide-react';
import { saveLibraryItem, saveAdCreative, uploadLibraryImage, loadLibraryItems, getBrandTags } from '../services/libraryService';
import { getPillarsByBrand } from '../services/pillarService';
import TagSelector from './TagSelector';

const AdCreationModal = ({
    isOpen,
    onClose,
    userId,
    brandId,
    initialData = {},
    onSuccess
}) => {
    const [step, setStep] = useState(1); // 1: Image Selection, 2: Details
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '', // This will be the caption
        hashtags: [],
        pillarId: '',
        selectedImage: null,
        imageFile: null
    });

    // Data State
    const [mediaAssets, setMediaAssets] = useState([]);
    const [pillars, setPillars] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Load initial data
    useEffect(() => {
        if (isOpen && brandId) {
            loadData();
        }
    }, [isOpen, brandId]);

    const loadData = async () => {
        setIsLoadingData(true);
        try {
            const [images, pillarsData, tags] = await Promise.all([
                loadLibraryItems(userId, brandId, { type: 'image' }),
                getPillarsByBrand(brandId),
                getBrandTags(userId, brandId)
            ]);

            // Filter out ad creatives from media assets
            setMediaAssets(images.filter(img => !img.isAdCreative));
            setPillars(pillarsData);
            setAvailableTags(tags.map(t => t.name));
        } catch (err) {
            console.error('Failed to load data:', err);
            setError('Failed to load necessary data');
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleImageSelect = (image) => {
        setFormData(prev => ({
            ...prev,
            selectedImage: image,
            imageFile: null
        }));
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    selectedImage: { imageUrl: reader.result, isNewUpload: true },
                    imageFile: file
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (!formData.selectedImage || !formData.title || !formData.pillarId) {
            setError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // 1. Handle Image Upload if needed
            let imageUrl = formData.selectedImage.imageUrl;
            if (formData.selectedImage.isNewUpload) {
                // saveLibraryItem handles base64 upload, but we can clarify it's an ad creative image
                // Or we can let saveLibraryItem handle it.
            }

            // 2. Prepare Ad Creative Object
            // saveAdCreative handles type assignment and metadata structure
            const adCreative = {
                title: formData.title,
                description: formData.description,
                imageUrl: imageUrl,
                tags: formData.hashtags,
                brandId,
                userId,
                pillarId: formData.pillarId,
                pillarName: pillars.find(p => p.id === formData.pillarId)?.name,
                status: 'draft',
                metadata: {
                    originalMediaId: !formData.selectedImage.isNewUpload ? formData.selectedImage.id : null
                }
            };

            // 3. Save
            await saveAdCreative(adCreative, userId, brandId);

            onSuccess && onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to save ad creative:', err);
            setError('Failed to create ad. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
        }}>
            <div style={{
                background: 'var(--color-bg-primary)',
                borderRadius: 'var(--radius-lg)',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'var(--shadow-2xl)'
            }}>
                {/* Header */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>Create New Ad Creative</h2>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            Step {step} of 2: {step === 1 ? 'Select Media' : 'Ad Details'}
                        </p>
                    </div>
                    <button onClick={onClose} className="btn btn-ghost btn-sm">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6)' }}>
                    {step === 1 ? (
                        <div className="animate-fadeIn">
                            {/* Image Upload Area */}
                            <div
                                style={{
                                    border: '2px dashed var(--color-border)',
                                    borderRadius: 'var(--radius-lg)',
                                    padding: 'var(--space-8)',
                                    textAlign: 'center',
                                    marginBottom: 'var(--space-6)',
                                    background: 'var(--color-bg-secondary)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => document.getElementById('ad-image-upload').click()}
                            >
                                <input
                                    id="ad-image-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <Upload size={32} style={{ margin: '0 auto var(--space-2) auto', color: 'var(--color-text-tertiary)' }} />
                                <div style={{ fontWeight: 500 }}>Upload New Media</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                    PNG, JPG up to 10MB
                                </div>
                            </div>

                            {/* Media Bank Grid */}
                            <h3 style={{ fontSize: 'var(--font-size-md)', marginBottom: 'var(--space-4)' }}>Or select from Media Bank</h3>
                            {isLoadingData ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                                    <Loader2 className="animate-spin" />
                                </div>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                    gap: 'var(--space-3)'
                                }}>
                                    {mediaAssets.map(img => (
                                        <div
                                            key={img.id}
                                            onClick={() => handleImageSelect(img)}
                                            style={{
                                                aspectRatio: '1',
                                                borderRadius: 'var(--radius-md)',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                border: formData.selectedImage?.id === img.id
                                                    ? '3px solid var(--color-primary)'
                                                    : '1px solid var(--color-border)',
                                                position: 'relative'
                                            }}
                                        >
                                            <img
                                                src={img.imageUrl}
                                                alt={img.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="animate-fadeIn" style={{ display: 'flex', gap: 'var(--space-6)' }}>
                            {/* Left: Image Preview */}
                            <div style={{ width: '300px', flexShrink: 0 }}>
                                {formData.selectedImage && (
                                    <img
                                        src={formData.selectedImage.imageUrl}
                                        alt="Selected"
                                        style={{
                                            width: '100%',
                                            borderRadius: 'var(--radius-lg)',
                                            boxShadow: 'var(--shadow-md)'
                                        }}
                                    />
                                )}
                                <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>SELECTED PILLAR</div>
                                    <div style={{ fontWeight: 500 }}>
                                        {pillars.find(p => p.id === formData.pillarId)?.name || 'None selected'}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Form */}
                            <div style={{ flex: 1 }}>
                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <label className="label">Ad Title</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="e.g. Summer Sale Promo 1"
                                    />
                                </div>

                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <label className="label">Content Pillar <span style={{ color: 'var(--error-500)' }}>*</span></label>
                                    <select
                                        className="select-field"
                                        value={formData.pillarId}
                                        onChange={(e) => setFormData({ ...formData, pillarId: e.target.value })}
                                    >
                                        <option value="">Select a pillar...</option>
                                        {pillars.map(pillar => (
                                            <option key={pillar.id} value={pillar.id}>{pillar.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <label className="label">Primary Text / Caption</label>
                                    <textarea
                                        className="input-field"
                                        style={{ minHeight: '120px', resize: 'vertical' }}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Enter the ad copy..."
                                    />
                                </div>

                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <label className="label">Hashtags</label>
                                    <TagSelector
                                        selectedTags={formData.hashtags}
                                        availableTags={availableTags}
                                        onTagsChange={(tags) => setFormData({ ...formData, hashtags: tags })}
                                        placeholder="Add hashtags..."
                                        onCreateTag={(tag) => setAvailableTags([...availableTags, tag])}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {error && <div style={{ color: 'var(--error-600)', fontSize: 'var(--font-size-sm)' }}>{error}</div>}
                    <div style={{ display: 'flex', gap: 'var(--space-3)', marginLeft: 'auto' }}>
                        {step === 2 && (
                            <button
                                className="btn btn-secondary"
                                onClick={() => setStep(1)}
                            >
                                Back
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            disabled={step === 1 ? !formData.selectedImage : loading}
                            onClick={() => {
                                if (step === 1) setStep(2);
                                else handleSubmit();
                            }}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    {step === 1 ? 'Next' : 'Create Ad'}
                                    {step === 1 ? <ImageIcon size={18} /> : <Save size={18} />}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdCreationModal;
